import {
    BaggageFare,
    BaggageFlight,
    BaggageSolution,
    Fare,
    FlightCard,
    FlightRouteInfo,
    FlightSegment,
    JsonsData,
    Sectors,
    SRPBaggageRequest
} from '@/src/types/flight'
import { convertDDMMYYYYToISO } from '@/src/utils/date/convertDateFormat'

export const getSRPBaggageRequest = (
    card: FlightCard,
    isMulticity: boolean,
    urlQueryObj: any,
    sectors: Sectors,
    formatDate: any,
    jsons: JsonsData,
    sFare?: Fare
): SRPBaggageRequest => {
    if (!card) {
        throw new Error('Invalid flight card data')
    }

    return {
        sc: {
            adt: Number(urlQueryObj.adults),
            chd: Number(urlQueryObj.childs),
            inf: Number(urlQueryObj.infants),
            intl: jsons.searchType?.isIntl ?? false,
            scr: jsons.searchType?.sellCurr,
            sct: jsons.searchType?.sellingCountry,
            tt: isMulticity ? 'MULTI_CITY' : undefined,
            stype: 'B2C',
            usrId: jsons?.userInfo?.usrId ?? ''
        },
        sol: buildSolutionFromCard(card, sectors, formatDate, isMulticity, sFare)
    }
}

const buildSolutionFromCard = (
    card: FlightCard,
    sectors: Sectors,
    formatDate: any,
    isMulticity: boolean,
    sFare?: Fare
): BaggageSolution[] => {
    const fare = sFare || (card.priceBreakup.fare as Fare)
    const bc = fare.bc?.split(',')
    let cabinType = 'Economy'

    const buildFlight = (sectorKey: string): BaggageFlight => {
        const sector: FlightRouteInfo = sectors[sectorKey]
        const segments = sector.flights.segments.map((segment: FlightSegment, index) => {
            cabinType = segment.flightClass
            const [airLineCode, _] = segment.flightNumber.split('-')
            // Multicity segments are keyed by fareBasisCode only (no per-segment fareKey).
            return convertSegment(
                segment,
                bc?.[index] || bc?.at(0) || '',
                fare.fb,
                isMulticity ? '' : fare.fk,
                airLineCode
            )
        })
        return {
            seg: segments
        }
    }

    const buildFare = (): BaggageFare => {
        const fre: BaggageFare = {
            fk: fare.fk,
            ctype: cabinType?.charAt(0),
            ncib: fare.ncib,
            mi: false
        }
        // Multicity sends the combined fare basis code alongside each leg's fare.
        if (isMulticity) {
            fre.combo_fbc = fare.fb
        }
        return fre
    }

    // Multicity: each leg (sectorKey) becomes its own solution with a single flight + its own fare.
    if (isMulticity) {
        return card.sectorKeys.map(sectorKey => {
            const flight = buildFlight(sectorKey)
            return {
                fl: [flight],
                fre: buildFare()
            }
        })
    }

    // One-way / round-trip: all legs share a single solution.
    const fl = card.sectorKeys.map(buildFlight)
    return [
        {
            fl,
            fre: buildFare()
        }
    ]
}

const convertSegment = (
    segment: FlightSegment,
    bc: string,
    fareBasisCode: string,
    fareKey: string,
    airlineCode: string
) => {
    // Combine date and time to create the required format
    const departureDateTime = `${convertDDMMYYYYToISO(segment.departure.date)} ${segment.departure.time}`
    const arrivalDateTime = `${convertDDMMYYYYToISO(segment.arrival.date)} ${segment.arrival.time}`

    const segmentObj: any = {
        dep: segment.departure?.airportCode,
        arr: segment.arrival?.airportCode,
        dd: departureDateTime,
        ad: arrivalDateTime,
        fn: segment.flightNumber?.split('-')[1],
        al: airlineCode,
        oa: segment.oa,
        bc: bc
    }

    if (fareBasisCode) {
        segmentObj.fareBasisCode = fareBasisCode
    }
    if (fareKey) {
        segmentObj.fareKey = fareKey
    }

    return segmentObj
}
